import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Grid, makeStyles } from '@material-ui/core';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import * as Popover from '../components/PopoverMenu';

import { substituteConductorColumns } from '../table-configs/columns-schemas';

import { fetchLine, setConductorDefaultExpirationDate } from 'app/store/slices/line';
import { URLS } from 'app/config/urls';
import lineEndpoint from 'app/api/services/lineEndpoint';
import helpers from '../../../utils/helpers';
import ToggleActiveModal from './modals/ToggleActiveModal';
import { activationPermissionaryValidationSchema } from '../form-configs/validation-schemas';
import notification from '../../../config/notification';
import { errorHandler } from '../../../utils/errorHandler';
import Loading from '../../../layouts/Loading';
import InfoLineCompany from '../components/InfoLineCompany';
import SaveBox from '../components/SaveBox';
import { useTableReducer } from '../hooks/useTableReducer';

const TODAY = moment(new Date()).format();

const ConductorsList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [conductors, actions] = useTableReducer();
    const { changePage, changePageSize, loadData: loadConductors } = actions;

    const cdLinhaFromPath = React.useRef(match.params.cdLinha);
    const selectedLine = useSelector((state) => state.line.selected);
    const activeUser = useSelector((state) => state.auth.registrationInfo);
    const defaultExpirationDate = useSelector((state) => state.line.conductorDefaultExpirationDate);

    const [loading, setLoading] = useState(false);
    const [loadingRows, setLoadingRows] = useState(true);
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('condutorSubstituto.cdCondutorSubstituto');
    const [orderDirection, setOrderDirection] = useState('desc');

    const [openActivateModal, setOpenActivateModal] = useState(false);

    const [filters, setFilters] = useState(() => {
        const result = {
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
        if (cdLinhaFromPath && cdLinhaFromPath.current) {
            result.cdLinha = Number(cdLinhaFromPath.current);
        }
        return result;
    });
    const [rows, setRows] = useState([]);
    const open = Boolean(editEl);

    useEffect(() => {
        (async () => {
            setLoading(false);
            if (cdLinhaFromPath && cdLinhaFromPath.current) {
                await dispatch(fetchLine(cdLinhaFromPath.current));
            }
            setLoading(false);
        })();
    }, [dispatch]);

    useEffect(() => {
        (async () => await fetch())();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conductors.page, conductors.pageSize, filters]);

    useEffect(() => {
        (() => {
            setRows(prepareRows(conductors.content));
        })();
    }, [conductors]);

    useEffect(() => {
        (async () => {
            if (!defaultExpirationDate || defaultExpirationDate === '') {
                const response = await lineEndpoint.getDefaultExpirationDate();
                dispatch(setConductorDefaultExpirationDate(response?.data));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            const temp = { ...row };
            temp.deCpf = helpers.formatarDocumento(temp.deCpf);
            return temp;
        });
    };

    async function fetch() {
        setLoadingRows(true);
        const params = {
            page: filters.page ? filters.page : conductors.page,
            size: filters.pageSize ? filters.pageSize : conductors.pageSize,
            request: filters
        };
        const response = await lineEndpoint.getSubstituteConductors(params);
        if (response.status === 200) {
            loadConductors(response?.data);
        }
        setLoadingRows(false);
    }

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `condutorSubstituto.${property}`;
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePage(null, 0);
    };

    const handleNewItineraryButton = async () => {
        const url = generatePath(URLS.LINES_MANAGE_CONDUCTORS_NEW, {
            cdLinha: match.params.cdLinha,
        });
        history.push(url);
    };

    const handleSubmitToggleActivation = async (values) => {
        try {
            setLoading(true);
            const isActivating = values.boAtivo === false;
            const payload = {
                boAtivar: !values.boAtivo,
                cdCondutorSubstituto: values.cdCondutorSubstituto,
                cdUsuarioResponsavel: activeUser.cdUsuario,
                deMotivo: values?.deMotivo || '',
                deObservacao: values?.deObservacao || '',
                dtCadastro: values?.dtCadastro || TODAY,
            };

            const response = await lineEndpoint.inactivateConductor(payload);

            if (response.status === 200) {
                await fetch();
                notification(`Condutor substituto ${isActivating ? 'ativado' : 'inativado'} com sucesso`, {
                    toastId: 'CONDUCTOR_ACTIVATION',
                    type: 'success',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        setLoading(false);
        setOpenActivateModal(false);
    };

    const handleClickToggleActivation = async () => {
        const boAtivo = selectedEntry?.boAtivo;
        if (!boAtivo) {
            await handleSubmitToggleActivation(selectedEntry);
        } else {
            setOpenActivateModal(true);
        }
    };

    const handleGoBack = () => {
        history.push(generatePath(URLS.LINES_MANAGE));
    };

    return (
        <>
            {loading && <Loading />}
            <PageWrapper
                title="Lista de condutor(es) substituto(s)"
                actionButton={
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.addButton}
                        onClick={handleNewItineraryButton}>
                        Novo
                    </Button>
                }>
                <Grid container spacing={4}>
                    <Grid item container spacing={3} direction={'column'}>
                        <InfoLineCompany selectedLine={selectedLine} />
                    </Grid>

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={substituteConductorColumns}
                            rows={rows}
                            loading={loadingRows}
                            onMore={handleOpenEditMenu}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: conductors.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : conductors.pageSize,
                                page: filters.page ? filters.page : conductors.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>

            <ToggleActiveModal
                isCurrentActive={selectedEntry?.boAtivo}
                open={openActivateModal}
                onClose={() => setOpenActivateModal(false)}
                data={selectedEntry}
                onSubmit={handleSubmitToggleActivation}
                conductor={true}
                formValidationSchema={activationPermissionaryValidationSchema}
                formInitialValues={{
                    cdLinha: selectedEntry?.cdLinha,
                    deMotivo: '',
                    deObservacao: '',
                    dtCadastro: TODAY,
                }}
            />
            <Popover.ConductorsList
                onEdit={handleOpenEditMenu}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/conductors`}
                dataId={selectedEntry?.cdCondutorSubstituto}
                data={selectedEntry}
                onClickActivate={handleClickToggleActivation}
                isActive={selectedEntry?.boAtivo}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default ConductorsList;
